// MyGroups Component - User's groups list

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Users, Search, Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getUserGroups, getUserInvitations } from '../../services/groupsService';
import type { Group, GroupInvitation } from '../../types/groups';
import { GroupCard } from './GroupCard';

interface MyGroupsProps {
  onBack: () => void;
  onViewGroup: (groupId: string) => void;
  onCreateGroup: () => void;
  onExploreGroups: () => void;
}

export function MyGroups({
  onBack,
  onViewGroup,
  onCreateGroup,
  onExploreGroups,
}: MyGroupsProps) {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [invitations, setInvitations] = useState<GroupInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'groups' | 'invitations'>('groups');

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const [userGroups, userInvitations] = await Promise.all([
          getUserGroups(user.uid),
          getUserInvitations(user.uid),
        ]);
        setGroups(userGroups);
        setInvitations(userInvitations);
      } catch (err) {
        console.error('Error loading groups:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Anmeldung erforderlich
          </h2>
          <p className="text-gray-500">
            Melde dich an, um deine Gruppen zu sehen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Meine Gruppen</h1>
            <div className="flex-1" />
            <button
              onClick={onExploreGroups}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              onClick={onCreateGroup}
              className="p-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('groups')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'groups'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Gruppen ({groups.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors relative ${
                activeTab === 'invitations'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Einladungen
              {invitations.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {invitations.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'groups' ? (
          <>
            {groups.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {groups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    onClick={() => onViewGroup(group.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Noch keine Gruppen
                </h3>
                <p className="text-gray-500 mb-4">
                  Tritt einer Gruppe bei oder erstelle deine eigene!
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={onExploreGroups}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Gruppen entdecken
                  </button>
                  <button
                    onClick={onCreateGroup}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Gruppe erstellen
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {invitations.length > 0 ? (
              <div className="space-y-4">
                {invitations.map((invite) => (
                  <InvitationCard
                    key={invite.id}
                    invitation={invite}
                    onViewGroup={onViewGroup}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Bell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Keine Einladungen
                </h3>
                <p className="text-gray-500">
                  Wenn dich jemand zu einer Gruppe einlaedt, erscheint die Einladung hier.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Invitation Card
interface InvitationCardProps {
  invitation: GroupInvitation;
  onViewGroup: (groupId: string) => void;
}

function InvitationCard({ invitation, onViewGroup }: InvitationCardProps) {
  const [responding, setResponding] = useState(false);

  const handleAccept = async () => {
    setResponding(true);
    // TODO: implement accept
    setResponding(false);
  };

  const handleDecline = async () => {
    setResponding(true);
    // TODO: implement decline
    setResponding(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
          <Users className="w-6 h-6 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onViewGroup(invitation.groupId)}
            className="font-semibold text-gray-900 hover:text-indigo-600"
          >
            {invitation.groupName}
          </button>
          <p className="text-sm text-gray-500">
            Eingeladen von {invitation.inviterName}
          </p>
          {invitation.message && (
            <p className="text-sm text-gray-600 mt-1 italic">
              "{invitation.message}"
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={handleAccept}
          disabled={responding}
          className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 transition-colors"
        >
          Annehmen
        </button>
        <button
          onClick={handleDecline}
          disabled={responding}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Ablehnen
        </button>
      </div>
    </div>
  );
}

export default MyGroups;
